import React, { useState } from 'react';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';

const BLANK = { label: '', field_type: 'dropdown', optionsText: '', allow_other: false, is_required: true };

/**
 * Manage the extra details a session asks candidates for before they start
 * (department, site, dosage form, ...).
 *
 * Fields belong to the session rather than the account, because the option lists
 * differ per customer. "Copy from another session" exists so a list of two dozen
 * departments is set up once and reused rather than retyped.
 */
const IntroFieldsPanel = ({ sessionId, fields, sessions, onChanged }) => {
  const [modal, setModal] = useState(null);
  const [copyModal, setCopyModal] = useState(null);
  const [busy, setBusy] = useState(false);

  const openNew = () => setModal({ ...BLANK });
  const openEdit = (f) => setModal({
    id: f.id,
    label: f.label,
    field_type: f.field_type,
    optionsText: (f.options || []).join('\n'),
    allow_other: !!f.allow_other,
    is_required: !!f.is_required,
  });

  const save = async () => {
    const options = modal.optionsText.split('\n').map(o => o.trim()).filter(Boolean);
    if (!modal.label.trim()) { alert('Please enter a label'); return; }
    if (modal.field_type === 'dropdown' && options.length === 0) {
      alert('A dropdown needs at least one option'); return;
    }
    setBusy(true);
    try {
      const payload = {
        label: modal.label.trim(),
        field_type: modal.field_type,
        options,
        allow_other: modal.allow_other,
        is_required: modal.is_required,
      };
      if (modal.id) await api.put(`/sessions/${sessionId}/intro-fields/${modal.id}`, payload);
      else await api.post(`/sessions/${sessionId}/intro-fields`, payload);
      setModal(null);
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Remove "${f.label}"? Details already collected are kept.`)) return;
    try {
      await api.delete(`/sessions/${sessionId}/intro-fields/${f.id}`);
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove');
    }
  };

  const move = async (f, delta) => {
    const idx = fields.findIndex(x => x.id === f.id);
    const target = fields[idx + delta];
    if (!target) return;
    try {
      await api.put(`/sessions/${sessionId}/intro-fields/${f.id}`, {
        label: f.label, field_type: f.field_type, options: f.options,
        allow_other: f.allow_other, is_required: f.is_required,
        order_index: target.order_index,
      });
      await api.put(`/sessions/${sessionId}/intro-fields/${target.id}`, {
        label: target.label, field_type: target.field_type, options: target.options,
        allow_other: target.allow_other, is_required: target.is_required,
        order_index: f.order_index,
      });
      onChanged();
    } catch (err) {
      alert('Failed to reorder');
    }
  };

  const doCopy = async () => {
    if (!copyModal.from) { alert('Pick a session to copy from'); return; }
    setBusy(true);
    try {
      const res = await api.post(`/sessions/${sessionId}/intro-fields/copy`, {
        from_session_id: parseInt(copyModal.from, 10),
        replace: copyModal.replace,
      });
      alert(res.data.message);
      setCopyModal(null);
      onChanged();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to copy');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="flex flex-between flex-center" style={{ marginBottom: '12px' }}>
        <div>
          <h3 style={{ margin: 0 }}>Extra Details Asked Before the Exam</h3>
          <p className="text-muted text-sm" style={{ marginTop: '4px', marginBottom: 0 }}>
            Collected from each candidate at the start, and reportable afterwards.
          </p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-secondary"
            onClick={() => setCopyModal({ from: '', replace: false })}>
            Copy from session
          </button>
          <button className="btn btn-sm btn-primary" onClick={openNew}>+ Add question</button>
        </div>
      </div>

      {fields.length === 0 ? (
        <p className="text-muted text-sm">
          None yet. Candidates go straight into the exam. Add a question to capture
          things like department or site.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '30px' }}>#</th>
              <th>Question</th>
              <th>Type</th>
              <th>Choices</th>
              <th style={{ textAlign: 'center' }}>Required</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f, i) => (
              <tr key={f.id}>
                <td className="text-muted">{i + 1}</td>
                <td style={{ fontWeight: 500 }}>{f.label}</td>
                <td className="text-sm text-muted">
                  {f.field_type === 'dropdown' ? 'Dropdown' : f.field_type === 'textarea' ? 'Long text' : 'Text'}
                  {f.allow_other && <span> + Other</span>}
                </td>
                <td className="text-sm text-muted">
                  {f.field_type === 'dropdown'
                    ? `${(f.options || []).length} option${(f.options || []).length === 1 ? '' : 's'}`
                    : '—'}
                </td>
                <td style={{ textAlign: 'center' }}>{f.is_required ? 'Yes' : 'No'}</td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn btn-sm btn-secondary" disabled={i === 0}
                    onClick={() => move(f, -1)} title="Move up">↑</button>{' '}
                  <button className="btn btn-sm btn-secondary" disabled={i === fields.length - 1}
                    onClick={() => move(f, 1)} title="Move down">↓</button>{' '}
                  <button className="btn btn-sm btn-secondary" onClick={() => openEdit(f)}>Edit</button>{' '}
                  <button className="btn btn-sm btn-danger" onClick={() => remove(f)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {modal && (
        <div className="modal-overlay" {...overlayDismiss(() => setModal(null))}>
          <div className="modal" style={{ maxWidth: '560px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal.id ? 'Edit Question' : 'New Question'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Question / label *</label>
                <input type="text" value={modal.label} maxLength={255}
                  onChange={e => setModal({ ...modal, label: e.target.value })}
                  placeholder="e.g. Department" />
              </div>

              <div className="form-group">
                <label>Answer type</label>
                <select value={modal.field_type}
                  onChange={e => setModal({ ...modal, field_type: e.target.value })}>
                  <option value="dropdown">Dropdown — pick one</option>
                  <option value="text">Text — single line</option>
                  <option value="textarea">Text — multi-line</option>
                </select>
              </div>

              {modal.field_type === 'dropdown' && (
                <>
                  <div className="form-group">
                    <label>Choices — one per line</label>
                    <textarea rows="8" value={modal.optionsText}
                      onChange={e => setModal({ ...modal, optionsText: e.target.value })}
                      placeholder={'Quality Assurance\nQuality Control\nManufacturing'} />
                    <p className="text-sm text-muted" style={{ marginTop: '6px' }}>
                      {modal.optionsText.split('\n').filter(o => o.trim()).length} choice(s). Paste a
                      list straight in — one per line.
                    </p>
                  </div>
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={modal.allow_other}
                        onChange={e => setModal({ ...modal, allow_other: e.target.checked })}
                        style={{ width: '18px', height: '18px' }} />
                      Offer &ldquo;Other&rdquo; with a free-text box
                    </label>
                    <p className="text-sm text-muted" style={{ marginTop: '4px' }}>
                      Useful when you do not yet know every value a customer uses.
                    </p>
                  </div>
                </>
              )}

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={modal.is_required}
                    onChange={e => setModal({ ...modal, is_required: e.target.checked })}
                    style={{ width: '18px', height: '18px' }} />
                  Required — the exam cannot start without it
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={busy}>
                {busy ? 'Saving...' : (modal.id ? 'Save Changes' : 'Add Question')}
              </button>
            </div>
          </div>
        </div>
      )}

      {copyModal && (
        <div className="modal-overlay" {...overlayDismiss(() => setCopyModal(null))}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Copy Questions From Another Session</h3>
              <button className="modal-close" onClick={() => setCopyModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Copy from</label>
                <select value={copyModal.from}
                  onChange={e => setCopyModal({ ...copyModal, from: e.target.value })}>
                  <option value="">Select a session...</option>
                  {(sessions || []).filter(s => s.id !== sessionId).map(s => (
                    <option key={s.id} value={s.id}>{s.session_name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={copyModal.replace}
                    onChange={e => setCopyModal({ ...copyModal, replace: e.target.checked })}
                    style={{ width: '18px', height: '18px' }} />
                  Replace the questions already here
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCopyModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={doCopy} disabled={busy}>
                {busy ? 'Copying...' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntroFieldsPanel;
